import {useMemo} from 'react'
import {View, type StyleProp, type ViewStyle} from 'react-native'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {getBadgesForActor} from '#/lib/badges/getBadgesForActor'
import type {Badge} from '#/lib/badges/badgeTypes'
import {atoms as a, useTheme} from '#/alf'
import {Button} from '#/components/Button'
import {useDialogControl} from '#/components/Dialog'
import {Text} from '#/components/Typography'
import {BadgeDetailsSheet} from './BadgeDetailsSheet'
import {getBadgeVisual} from './badgeUtils'

type ActorLike = Parameters<typeof getBadgesForActor>[0]

export function BadgeRow({
  actor,
  badges,
  style,
}: {
  actor?: ActorLike
  badges?: Badge[]
  style?: StyleProp<ViewStyle>
}) {
  const t = useTheme()
  const {_} = useLingui()
  const control = useDialogControl()
  const resolvedBadges = useMemo(() => {
    if (badges) return badges
    return actor ? getBadgesForActor(actor) : []
  }, [actor, badges])

  if (!resolvedBadges.length) return null

  const openDetails = () => control.open()

  return (
    <View style={[a.flex_row, a.flex_wrap, a.gap_xs, style]}>
      {resolvedBadges.map(badge => {
        const {Icon, color} = getBadgeVisual(badge, t)
        return (
          <Button
            key={`${badge.source}-${badge.type}-${badge.org?.id || ''}`}
            label={badge.label}
            accessibilityHint={_(msg`Opens badge details`)}
            onPress={openDetails}>
            {({hovered, pressed}) => (
              <View
                style={[
                  a.flex_row,
                  a.align_center,
                  a.rounded_full,
                  a.px_sm,
                  a.py_xs,
                  a.gap_xs,
                  t.atoms.bg_contrast_25,
                  (hovered || pressed) && t.atoms.bg_contrast_50,
                ]}>
                <Icon width={14} height={14} fill={color} />
                <Text
                  style={[
                    a.text_xs,
                    a.font_bold,
                    a.leading_tight,
                    t.atoms.text_contrast_medium,
                  ]}>
                  {badge.label}
                </Text>
              </View>
            )}
          </Button>
        )
      })}

      <BadgeDetailsSheet control={control} badges={resolvedBadges} />
    </View>
  )
}
